import React, { useState, useCallback, useMemo } from 'react';
import Button from 'components/DesignSystem/Button';
import Checkbox from 'components/DesignSystem/Checkbox';
import About from 'components/Meteorology/Agro/About';
import useChartVariable from 'hooks/Meteorology/Agro/WaterBalanceSequential/DetailedView/useChartVariable';
import useDownloadDivImage from 'hooks/useDownloadDivImage';

export default function ChartControls({ imageRef, hasCharts }: {
  imageRef: React.RefObject<HTMLDivElement | null>;
  hasCharts: boolean;
}) {
  const {
    chartOptions,
    selectedChartVariables,
    allSelected,
    toggleChartVariables,
    selectAll,
    deselectAll,
  } = useChartVariable();

  // Filter out chuva and temperatura from individual display
  const displayChartOptions = useMemo(
    () => chartOptions.filter(
      (option) => option.value !== 'chuva' && option.value !== 'temperatura',
    ),
    [chartOptions],
  );

  // Check if both chuva and temperatura exist in chartOptions
  const chuvaOption = useMemo(
    () => chartOptions.find((o) => o.value === 'chuva'),
    [chartOptions],
  );
  const temperaturaOption = useMemo(
    () => chartOptions.find((o) => o.value === 'temperatura'),
    [chartOptions],
  );
  const hasCombinedOption = useMemo(
    () => !!chuvaOption && !!temperaturaOption,
    [chuvaOption, temperaturaOption],
  );

  // Check if combined meteorological variables are selected
  const isCombinedSelected = useMemo(() => {
    if (!hasCombinedOption || !chuvaOption || !temperaturaOption) return false;
    return selectedChartVariables.includes(chuvaOption.id)
      && selectedChartVariables.includes(temperaturaOption.id);
  }, [
    selectedChartVariables,
    hasCombinedOption,
    chuvaOption,
    temperaturaOption,
  ]);

  const toggleCombinedVariables = useCallback(() => {
    if (!hasCombinedOption || !chuvaOption || !temperaturaOption) return;

    if (isCombinedSelected) {
      toggleChartVariables(chuvaOption.id);
      toggleChartVariables(temperaturaOption.id);
    } else {
      if (!selectedChartVariables.includes(chuvaOption.id)) {
        toggleChartVariables(chuvaOption.id);
      }
      if (!selectedChartVariables.includes(temperaturaOption.id)) {
        toggleChartVariables(temperaturaOption.id);
      }
    }
  }, [
    hasCombinedOption,
    isCombinedSelected,
    toggleChartVariables,
    chuvaOption,
    temperaturaOption,
    selectedChartVariables,
  ]);

  const downloadChartsAsImage = useDownloadDivImage(imageRef);
  const selectedVariableNames = selectedChartVariables
    .map((variableId) => chartOptions.find((o) => o.id === variableId)?.value)
    .filter((v) => v) as string[];
  const filename = `balanco_hidrico_sequencial_${selectedVariableNames.join('_')}.png`;
  const [showAbout, setShowAbout] = useState(false);

  return (
    <>
      <About
        show={showAbout}
        onHide={() => setShowAbout(false)}
      />
      <div className="d-flex align-items-center mb-4 px-5">
        <div className="d-flex flex-row dvcard-radios">
          {hasCombinedOption && (
            <Checkbox
              key="chuva-temperatura"
              label="Chuva e Temperatura"
              onChange={toggleCombinedVariables}
              checked={isCombinedSelected}
              disabled={!hasCharts}
            />
          )}
          {displayChartOptions.map((option) => (
            <Checkbox
              key={option.value}
              label={option.label}
              onChange={() => { toggleChartVariables(option.id); }}
              checked={selectedChartVariables.includes(option.id)}
              disabled={!hasCharts}
            />
          ))}
          <Checkbox
            key="all"
            label="Todos"
            onChange={() => { if (allSelected) { deselectAll(); } else { selectAll(); } }}
            checked={allSelected}
            disabled={!hasCharts}
          />
        </div>
        <Button
          icon="download"
          small
          type="tertiary"
          id="chart-download-button"
          className="ms-auto"
          onClick={downloadChartsAsImage(filename)}
          disabled={selectedChartVariables.length === 0 || !hasCharts}
        />
        <Button
          id="water-balance-about"
          className="ms-2"
          icon="info"
          onClick={() => setShowAbout(true)}
        />
      </div>
    </>
  );
}
